import * as React from 'react'
import { useTemperatureUnitContext } from 'context/TemperatureUnitContext';
import {
    VictoryArea,
    VictoryAxis, VictoryBar,
    VictoryChart,
    VictoryGroup,
    VictoryLabel,
    VictoryLine,
    VictoryContainer,
    VictoryScatter, VictoryTooltip, VictoryVoronoiContainer
} from 'victory'
import isBrowser from 'services/isBrowser';
import { WeatherData } from 'templates/fragments/weather-data';
import { convertToDegrees, convertToFahrenheit, convertToInches } from 'services/convertUnits'
import _ from 'lodash';
import { months, monthSlugs, measures } from 'services/centralStore';
import firstCapital from 'services/firstCapitalCase';
import { IntlShape, useIntl } from 'gatsby-plugin-react-intl'
import { text } from 'stream/consumers';
import Select, { CommonProps, components, GroupTypeBase, OptionTypeBase } from 'react-select'
import { useElementWidth } from 'hooks/useElementWidth';
import { measureColors as colorMapping } from 'services/measureColors';
import { prepareNumber } from 'services/prepareNumber';

let domains: [number, number][];
let selectedUnit: string;
let measuresShown: string[] = null;
let singleMeasure: string = null;
const chartData: { [key: string]: DataForChart[] } = {};


const widthBreakpoints = {
    allowMeasureSelection: 750,
    chartHeightBreakpoints: [[400,270],[600,340],[750,420],['default',540]],
    showAllMonths: 1250,
    showMonthAbbrev: 750,
    justOddValues: 400,
    smallerAxisFonts: 400,
    fullWidthChart: 750,
    showJustMinMax: 750
};
const defaultLabelStyle: any = { fontFamily: '"dunbar-text-normal", sans-serif' }

export const Chart = ({ weatherData, fixedMeasures, chartWrapperRefOuter,title }: { weatherData: WeatherData, chartWrapperRefOuter?: any, fixedMeasures?: string[],title?: string }) => {
    const intl = useIntl()
    const currentMeasures = fixedMeasures || measures;
    if (fixedMeasures && fixedMeasures.length == 1) {
        singleMeasure = fixedMeasures[0];
    }
    else {
        singleMeasure = null;
    }


    let fixedShownMeasuresState: { [key: string]: boolean } = null;
    if (fixedMeasures) {
        fixedShownMeasuresState = {};
        for (let measure of fixedMeasures) {
            fixedShownMeasuresState[measure] = true;
        }
    }
    // states
    const [shownMeasuresState, setShownMeasuresState] = React.useState(fixedShownMeasuresState || updateMeasuresFromSelector('overview'));
    const [chartWidth, setChartWidth] = React.useState(600);
    const [measureSelectorValue, setMeasureSelectorValue] = React.useState('overview');
    const [unit] = useTemperatureUnitContext()

    // refs
    const chartWrapperRef = React.useRef(null);
    const measureSelectorRef = React.useRef(null);


    // resizing
    useElementWidth(chartWrapperRef, setChartWidth)


    // calculations
    selectedUnit = unit;
    const measureOptions = currentMeasures
        .filter(m => (weatherData as any)[m][0] != null)
        .map(m => { return { value: m, label: `${firstCapital(intl.formatMessage({ id: 'common.measures.' + m }))}` } })
    const measureSelectorValues = [{ value: 'overview', label: `${firstCapital(intl.formatMessage({ id: 'common.weatherOverview'}))}` }, ...measureOptions];
    if (chartWidth > widthBreakpoints.allowMeasureSelection) measureSelectorValues.splice(1, 0, { value: 'detail', label: 'Weather detail' });

    domains = calculateDomains(weatherData,fixedMeasures)
    measuresShown = [];
    for (let measure of currentMeasures) {
        chartData[measure] = prepareDataForChart({ measure, weatherData });
        if (shownMeasuresState[measure]) {
            measuresShown.push(measure);
        }
    }

    for (let measure of currentMeasures) {
        fixLabelOverlaps({ measure, chartWidth });
    }
    let domainsMapping = getDomainMapping();
    let domainsToShow = domains.map((domain, idx) => _.intersection(domainsMapping[idx], measuresShown).length ? domain : null);
    // in case there is no first domain, third domain take its place
    let firstDomain = domainsToShow[0];
    let firstDomainSwitch = false;
    if (!firstDomain && domainsToShow.length > 2 && domainsToShow[2]) {
        firstDomainSwitch = true;
        firstDomain = domainsToShow[2];
    }
    let secondDomain = domainsToShow[1];
    let thirdDomain = (domainsToShow.length > 2 && !firstDomainSwitch) ? domainsToShow[2] : null;
    const monthsToShow = chartWidth > widthBreakpoints.showAllMonths ? monthSlugs.slice(0, 12) : monthSlugs.slice(0, 12).filter((a, i) => i % 2 == 0)
    const axisLabels = calculateAxisLabels({ intl });
    const randomKey = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
    const leftOffsets = chartWidth > widthBreakpoints.fullWidthChart ? [90,60,26] : [80,52,28];
    const rightOffsets = (chartWidth > widthBreakpoints.allowMeasureSelection) ? [56, 26] : [48, 18];
    const axisFontSize = (chartWidth > widthBreakpoints.allowMeasureSelection) ? 16 : 13;
    
    
    
    // render
    return (<div style={title ? {paddingTop:36} : undefined} className="destination__graph center" ref={chartWrapperRefOuter} >
        {/* <div className="destination__graph__checkbox">
            <label htmlFor="advancedGraph" onClick={handleAdvancedGraphCheck}><span className={`destination__graph__checkbox__toggle ${showAdvancedGraph && 'active'}`} />Ukázat pokročilý graf</label>
            <input type="checkbox" name="advancedGraph" id="advancedGraph" defaultChecked={showAdvancedGraph} />
        </div> */}
        {title && <div className="destination__graph__title">{title}</div>}

        {/* CHART */}
        <div className="destination__graph--wrapper" ref={chartWrapperRef}>
            <VictoryChart
                
                width={chartWidth}
                height={getChartHeight(chartWidth)}
                padding={{
                    top: 20, bottom: 40,
                    left: (firstDomain && thirdDomain) ? leftOffsets[0] : (firstDomain ? leftOffsets[1] : leftOffsets[2]),
                    right: secondDomain ? rightOffsets[0] : rightOffsets[1]
                }}
                domainPadding={{ y: [0, 0] }}
                style={{}}
                containerComponent={
                    <VictoryContainer
                        style={{
                            pointerEvents: "auto",
                            userSelect: "auto",
                            touchAction: "auto"
                        }}
                    />
                }
            // containerComponent={<VictoryVoronoiContainer voronoiDimension="x"
            //     responsive={false}
            //     mouseFollowTooltips={true}
            //     labels={({ datum }) => {
            //         let [measure,chartType] = datum.childName.split('-')
            //         if (chartType == 'scatter' || datum.x < 1 || datum.x > 12) return null;
            //         return `${measure}: ${datum.forTooltip}`
            //     }}
            //     labelComponent={<VictoryTooltip cornerRadius={0} flyoutStyle={{ fill: "white" }} />}
            // />
            // }
            >
                <VictoryAxis
                    domain={[0.5, 12.5]}
                    offsetY={40}
                    style={{ axis: { stroke: '#888'}, grid: { stroke: '#efefef', strokeDasharray: '5,5' }, tickLabels: { ...defaultLabelStyle,fontSize:axisFontSize }, axisLabel: { ...defaultLabelStyle } }}
                    tickValues={monthsToShow.length > 6 ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] : [1, 3, 5, 7, 9, 11]}
                    tickFormat={monthsToShow.map(m => `${m[0].toUpperCase()}${chartWidth > widthBreakpoints.showMonthAbbrev ? m.slice(1) : m.slice(1, 3)}`)}
                />
                {domains[0][0] < 0 && <VictoryAxis
                    domain={[0.5, 12.5]}
                    style={{
                        grid: { strokeOpacity: 0 },
                        tickLabels: { opacity: 0 }
                    }}
                    tickValues={[]}
                />

                }
                {firstDomain && <VictoryAxis
                    dependentAxis
                    domain={domains[0]}
                    label={axisLabels[firstDomainSwitch ? 2 : 0]}
                    tickValues={firstDomainSwitch ? [domains[0][0], domains[0][1]] : calculateTickValues()}
                    tickFormat={(val) => firstDomainSwitch ? convertToDomain(val, domains[2], true) : val}
                    style={{ grid: { stroke: '#efefef',strokeDasharray: '5,5'}, tickLabels: { ...defaultLabelStyle, fontSize: axisFontSize }, axisLabel: { padding: 36, ...defaultLabelStyle, fontSize: axisFontSize } }}
                />}
                {secondDomain && <VictoryAxis
                    dependentAxis
                    orientation={`right`}
                    label={axisLabels[1]}
                    domain={domains[0]}
                    tickValues={[domains[0][0], domains[0][1]]}
                    tickFormat={(val) => convertToDomain(val, domains[1], true)}
                    style={{ grid: { strokeOpacity: 0 }, tickLabels: { ...defaultLabelStyle, fontSize: axisFontSize }, axisLabel: { padding: 14, ...defaultLabelStyle, fontSize: axisFontSize } }}
                />}
                {(!firstDomain || !secondDomain) && <VictoryAxis
                    dependentAxis
                    orientation={firstDomain ? `right` : `left`}
                    tickValues={[]}
                    style={{ grid: { strokeOpacity: 0 }, tickLabels: { opacity: 0 } }}
                />}

                {thirdDomain && <VictoryAxis
                    offsetX={46}
                    dependentAxis
                    domain={domains[0]}
                    label={axisLabels[2]}
                    tickValues={[domains[0][0], domains[0][1]]}
                    tickFormat={(val) => convertToDomain(val, domains[2], true)}
                    style={{
                        axis: { strokeOpacity: 0 },
                        grid: { strokeOpacity: 0 },
                        axisLabel: { ...defaultLabelStyle, opacity: 0.5, fontSize: axisFontSize },
                        tickLabels: { ...defaultLabelStyle, opacity: 0.5, fontSize: axisFontSize }

                    }}
                />}
                {shownMeasuresState['ppt'] && renderBar({ measure: 'ppt', chartWidth })}
                {shownMeasuresState['tmax'] && renderLine({ measure: 'tmax', chartWidth })}
                {shownMeasuresState['tmin'] && renderLine({ measure: 'tmin', chartWidth })}
                {shownMeasuresState['sst'] && renderLine({ measure: 'sst', chartWidth })}
                {shownMeasuresState['wet'] && renderLine({ measure: 'wet', chartWidth })}
                {shownMeasuresState['sunhrs'] && renderLine({ measure: 'sunhrs', chartWidth })}
                {shownMeasuresState['rhm'] && renderLine({ measure: 'rhm', chartWidth })}
                {shownMeasuresState['ws'] && renderLine({ measure: 'ws', chartWidth })}

            </VictoryChart>
        </div>


        {/* MEASURE SELECTOR */}
        <form className="row graph__advanced--opts--wrapper">
            {currentMeasures.map(measure => {
                if ((_.includes(['ws', 'sst', 'rhm', 'sunhrs'], measure)) && (weatherData as any)[measure][0] == null) {
                    return null;
                }
                if (chartWidth < widthBreakpoints.allowMeasureSelection && !shownMeasuresState[measure]) return null;
                return (
                    <label key={measure} htmlFor={`${randomKey}-${measure}Input`}
                        className={`graph__advanced--opts ` + (((chartWidth < widthBreakpoints.allowMeasureSelection) && measuresShown.length == 1) ? `col--is--12` : `col--is--6 col--sm--3`)}
                    >
                        {chartWidth >= widthBreakpoints.allowMeasureSelection && <input type="checkbox"
                            id={`${randomKey}-${measure}Input`}
                            name={`${randomKey}-${measure}Input`}
                            checked={shownMeasuresState[measure]}
                            style={{
                                backgroundColor: colorMapping[measure]
                            }}
                            onChange={() => {
                                setShownMeasuresState(oldState => {
                                    let newState = { ...oldState };
                                    newState[measure] = !oldState[measure];
                                    return newState;
                                })
                                setMeasureSelectorValue(null);
                                measureSelectorRef.current.select.clearValue();

                            }} />}
                        <span
                            style={{
                                color: colorMapping[measure]
                            }}
                        >
                            {`${firstCapital(intl.formatMessage({ id: 'common.measures.' + measure }))}`}
                        </span>
                    </label>



                )
            })}
            {(chartWidth > widthBreakpoints.allowMeasureSelection) && <div className="col--is--12 measure__selector--wrapper">
                <Select
                    ref={measureSelectorRef}
                    isSearchable={false}
                    className="measure__selector"
                    menuPortalTarget={isBrowser() ? document.body : null}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    menuPlacement="top"

                    onChange={(opt) => {
                        setMeasureSelectorValue(opt?.value)
                        if (!opt?.value || !shownMeasuresState) return;
                        let newState = updateMeasuresFromSelector(opt?.value);
                        checkStateValues(newState, weatherData)
                        setShownMeasuresState(newState);
                    }}
                    value={measureSelectorValues.find(m => m.value == measureSelectorValue)}
                    options={measureSelectorValues}
                    name="name"
                    placeholder="Custom selection"
                />
            </div>}
        </form>
    </div>)
}


const calculateTickValues = (): number[] => {
    let val = domains[0][0];
    let ticks = [];
    let diff = domains[0][1] - domains[0][0];
    let frequency = diff > 16 ? 5 : 2;
    do {
        if (val % frequency == 0) {
            ticks.push(val);
        }
        val++;
    } while (val <= domains[0][1]);
    return ticks;
}

const checkStateValues = (state: { [key: string]: boolean }, weatherData: WeatherData) => {
    if (!weatherData.sst[0]) {
        state['sst'] = false;
    }
    if (!weatherData.sunhrs[0]) {
        state['sunhrs'] = false;
    }
    if (!weatherData.rhm[0]) {
        state['rhm'] = false;
    }
    if (!weatherData.ws[0]) {
        state['ws'] = false;
    }
}
const calculateDomains = (weatherData: WeatherData, fixedMeasures?: string[]): [number, number][] => {

    if (fixedMeasures && fixedMeasures.length == 1) {
        let min = Math.min(...((weatherData) as any)[fixedMeasures[0]]);
        let max = Math.max(...((weatherData) as any)[fixedMeasures[0]]);
        if (min < 6) min = 0;
        if (fixedMeasures[0] == 'rhm') {
            return [[Math.floor((min - 5) / 5) * 5, Math.ceil((max + 5) / 5) * 5]]
        }
        else {
            return [[Math.max(0,Math.floor((min - 2) / 2) * 2), Math.ceil((max + 2) / 2) * 2]]
        }
    }

    let isF = selectedUnit == 'F';
    let toF = convertToFahrenheit;
    let [tmax, tmin, sst] = [weatherData.tmax.map(t => isF ? toF(t) : t), weatherData.tmin.map(t => isF ? toF(t) : t), weatherData.sst.map(t => isF ? toF(t) : t)]
    sst = _.compact(sst);
    let [rhm, ws, sunhrs,wet] = [_.compact(weatherData.rhm), _.compact(weatherData.ws), _.compact(weatherData.sunhrs),weatherData.wet];
    let ppt = weatherData.ppt.map(p => isF ? convertToInches(p) : p);
    
    let degMin = Math.min(...tmax, ...tmin, ...sst);
    let degMax = Math.max(...tmax, ...tmin, ...sst);
    let domain: [number, number];
    let hrsDomain: [number, number] = null;
    let percDomain: [number, number] = null;

    // for C main domain consists of tmin, tmax, sst; secondary rhm, ppt, possible tertially sunhrs, wet, ws - otherwise part of first
    // for F main domain consists of tmin, tmax, sst, rhm; secondary sunhrs, wet, ws, ppt
    if (selectedUnit === 'F') {
        if (!fixedMeasures) {
            degMin = Math.min(degMin, ...rhm);
            degMax = Math.max(degMax, ...rhm);
        }
        domain = [Math.floor((degMin - 10) / 10) * 10, Math.ceil((degMax + 10) / 10) * 10];
        let hrsMax = Math.max(...ppt);
        if (!fixedMeasures) {
            hrsMax = Math.max(...sunhrs, ...wet, ...ws, ...ppt);
        }
        hrsDomain = [0, Math.ceil(hrsMax + 1)]
    }
    else {
        domain = [Math.min(degMin > 17 ? 15 : 0, Math.floor((degMin - 2) / 2) * 2), Math.ceil((degMax + 2) / 2) * 2];
        let percMax = Math.max(...ppt, ...rhm);
        if (domain[0] > 0) {
            if (!fixedMeasures) {
                let hrsMax = Math.max(...sunhrs, ...wet, ...ws);
                hrsDomain = [0, Math.ceil(hrsMax + 1)]
            }
        }
        else {
            if (!fixedMeasures) {
                percMax = Math.max(percMax, ...sunhrs, ...wet, ...ws);
            }
        }

        percDomain = [0, Math.max(100, Math.ceil((percMax + 10) / 10) * 10)];
    }

    return _.compact([domain, percDomain, hrsDomain]);
}

const calculateAxisLabels = ({ intl }: { intl: IntlShape }) => {
    let domainMapping = getDomainMapping();
    let domainLabels = [];
    let temperatureIncluded = false;
    for (let domain of domainMapping) {
        let texts: string[] = [];

        domain = domain.filter(d => _.includes(measuresShown, d));
        if ((_.includes(domain, 'tmin') || _.includes(domain, 'tmax') || _.includes(domain, 'sst')) && !temperatureIncluded) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.temperature' })) + ` (° ${selectedUnit})`);
            temperatureIncluded = true;
        }
        if (_.includes(domain, 'ws')) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.measures.ws' })));
        }
        if (_.includes(domain, 'rhm')) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.measures.rhm' })) + ' %');
        }
        if (_.includes(domain, 'sunhrs')) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.measures.sunhrs' })));
        }
        if (_.includes(domain, 'ppt')) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.measures.ppt' })) + ` (${selectedUnit == 'C' ? 'mm' : 'in'})`);
        }
        if (_.includes(domain, 'wet')) {
            texts.push(firstCapital(intl.formatMessage({ id: 'common.measures.wet' })));
        }

        domainLabels.push(texts.join(', '));
    }
    return domainLabels

}

const renderBar = ({ measure, chartWidth }: { measure: string, chartWidth: number }) => {
    let data = chartData[measure].slice(1, -1);
    let color = colorMapping[measure];
    return [<VictoryBar
        data={data}
        style={{ data: { stroke: color, strokeWidth: 4, fill: color } }}
        name={`${measure}-bar`}
        labelComponent={<VictoryLabel
            style={{
                textShadow: `-2px -2px white, -2px 2px white, 2px 2px white, 2px -2px white, -2px 0 white, 0 2px white, 2px 0 white, 0 -2px white`,
                color: colorMapping[measure],
                ...defaultLabelStyle,
                fontSize: getFontSize(chartWidth)
            }}
            dy={5}
            
            renderInPortal />
        }
    />]

}


const renderLine = ({ measure, chartWidth }: { measure: string, chartWidth: number }) => {
    let data = chartData[measure];
    let color = colorMapping[measure];
    return [<VictoryLine
        data={data}
        interpolation="monotoneX"
        style={{ data: { stroke: color, strokeWidth: chartWidth > widthBreakpoints.allowMeasureSelection ? 5 : 4 } }}
        name={`${measure}-line`}
        labelComponent={<VictoryLabel
            style={{
                //textShadow: `-2px -2px white, -2px 2px white, 2px 2px white, 2px -2px white, -2px 0 white, 0 2px white, 2x 0 white, 0 -2px white;`
                textShadow: `-2px -2px white, -2px 2px white, 2px 2px white, 2px -2px white, -2px 0 white, 0 2px white, 2px 0 white, 0 -2px white`,
                fill: colorMapping[measure],
                ...defaultLabelStyle,
                fontSize: getFontSize(chartWidth)
            }}
            dy={-5}
            renderInPortal />
        }
    />,
    <VictoryScatter style={{
        data: {
            fill: color,
            fillOpacity: 1,
            stroke: ({ datum }) => datum.isArtificial ? 'transparent' : color,
        }
    }}
        name={`${measure}-scatter`}
        data={data.slice(1, -1).map(d => { return { ...d, label: null }; })}
        size={4}

    />]

}

const getFontSize = (chartWidth: number) => {
    return (chartWidth > widthBreakpoints.showJustMinMax) ? 17 : 14
}

const updateMeasuresFromSelector = (selectedValue: string) => {
    if (!selectedValue) return null;
    let newState: { [key: string]: boolean } = {}
    for (let key of measures) {
        newState[key] = false;
    }
    switch (selectedValue) {
        case 'detail': {
            newState['wet'] = true;
            newState['sunhrs'] = true;
        }
        case 'overview': {
            newState['tmin'] = true;
            newState['tmax'] = true;
            newState['sst'] = true;
            newState['ppt'] = true;
            break;
        }
        default: {
            newState[selectedValue] = true;
        }
    }
    return newState;
}


const getDomainMapping = () => {
    let domainMapping: string[][]
    if (singleMeasure) {
        return [[singleMeasure]];
    }
    if (selectedUnit == 'F') {
        domainMapping = [['tmin', 'tmax',  'sst', 'rhm'], ['ws', 'sunhrs', 'wet','ppt']] //temperature, precipitation, humidity // wind, sun hours, wet days
    }
    else if (domains.length > 2) {
        domainMapping = [['tmin', 'tmax', 'sst'], ['rhm', 'ppt'], ['ws', 'sunhrs', 'wet']] //temperature // precipitation, humidity // wind, sun hours, wet days
    }
    else {
        domainMapping = [['tmin', 'tmax', 'sst', 'ws', 'sunhrs', 'wet'], ['rhm', 'ppt']] //temperature, wet days, sun hours, wind // precipitation, humidity
    }
    return domainMapping;
}


const prepareDataForChart = ({ measure, weatherData }: { measure: string, weatherData: WeatherData }): DataForChart[] => {
    let data: DataForChart[];
    let domainMapping = getDomainMapping();
    if (_.includes(domainMapping[0], measure)) {
        data = (weatherData as any)[measure].map((r: any, i: number): any => { return { x: i + 1, ...calculateValue({ measure, val: r }) } });
    }
    else if (domainMapping[1] && _.includes(domainMapping[1], measure)) {
        data = (weatherData as any)[measure].map((r: any, i: number): any => { return { x: i + 1, ...calculateValue({ measure, val: r, otherDomain: domains[1] }) } });

    }
    else if (domainMapping.length > 2 && _.includes(domainMapping[2], measure)) {
        data = (weatherData as any)[measure].map((r: any, i: number): any => { return { x: i + 1, ...calculateValue({ measure, val: r, otherDomain: domains[2] }) } });
    }
    let janDecAvg = (data[0].y + data[11].y) / 2
    data = [{ x: 0.5, y: janDecAvg, label: '', isArtificial: true }, ...data, { x: 12.5, y: janDecAvg, label: null, isArtificial: true }]

    return data;
}

const calculateValue = ({ measure, otherDomain, val }: { measure: string, otherDomain?: [number, number], val: number }) => {
    let baseDomain = domains[0]
    let precision = 0;
    if (_.includes(['sst', 'tmin', 'tmax'], measure)) {
        val = selectedUnit == 'F' ? convertToFahrenheit(val) : val;
        precision = 1;
    }
    if (_.includes(['ws', 'sunhrs', 'wet'], measure)) {
        precision = 1;
    }
    if (measure == 'ppt') {
        val = selectedUnit == 'F' ? convertToInches(val) : val;
        precision = selectedUnit == 'F' ? 2 : 0;
    }
    let originalVal = val;
    val = otherDomain ? convertToDomain(val, otherDomain) : val;
    let valFixed = originalVal == null ? null : prepareNumber(originalVal, precision);
    let ret: { y: number, label: string, forTooltip: string, y0?: number } = { y: val, label: valFixed, forTooltip: valFixed }
    if (measure == 'ppt') {
        ret.y0 = domains[0][0]+0.1;
    }
    return ret;
}

const showJustOneMeasure = ({ measure }: { measure: string }) => {
    let measureToLabel = measuresShown[0];
    let isMeasureToLabel = measureToLabel == measure;
    let data = chartData[measure];
    let vals = data.map(d => d.y)
    //let [min, max] = [Math.min(...vals), Math.max(...vals)];
    for (let i = 1; i < 13; i++) {
        //let val = data[i].y;
        if (!isMeasureToLabel) {
            data[i].label = '';
            continue;
        }
        
        let val = parseFloat(data[i].label);
        if (isNaN(val)) continue;
        data[i].label = prepareNumber(val,0);
    }
}

const fixLabelOverlaps = ({ measure, chartWidth }: { measure: string, chartWidth: number }) => {
    let measuresToLabel = measuresShown.slice(0);
    let isMeasureToLabel = _.includes(measuresToLabel, measure);
    let otherMeasures = isMeasureToLabel ? measuresToLabel.slice(0, measuresToLabel.indexOf(measure)) : [];
    let data = chartData[measure];
    let limitValue = (domains[0][1] - domains[0][0]) / 16;
    let isSmall = chartWidth < widthBreakpoints.showJustMinMax;
    for (let i = 1; i < 13; i++) {
        let conflicting = false;
        let val = data[i].y;
        
        if (!isMeasureToLabel) {
            data[i].label = '';
            continue;
        }
        if (measure == 'ppt' && val < 0) {
            data[i].label = '';
            continue;
        }
        if (chartWidth <= widthBreakpoints.justOddValues && i % 2 == 0) {
            data[i].label = '';
            continue;
        }
        for (let otherMeasure of otherMeasures) {
            if (isSmall && otherMeasure == 'ppt') {
                continue;
            }
            let otherVal = chartData[otherMeasure][i].y;
            if (otherVal == null) continue;
            if (Math.abs(val - otherVal) < limitValue) {
                //console.log(measure, otherMeasure, val, otherVal)
                conflicting = true;

                break;
            }
        }
        if (conflicting || (isSmall && measure=='ppt')) {
            data[i].label = '';
            continue;
        }
        
        if (isSmall) {
            let val = parseFloat(data[i].label);
            if (isNaN(val)) continue;
            data[i].label = prepareNumber(val, 0);
        }
    }
}

const convertToDomain = (val: number, domain: [number, number], switched?: boolean) => {
    let baseDomain = switched ? domain : domains[0];
    domain = switched ? domains[0] : domain;
    return ((val - domain[0]) * (baseDomain[1] - baseDomain[0]) / (domain[1] - domain[0]) + baseDomain[0])
}

const getChartHeight = (clientWidth: number) => {
    for (let pair of widthBreakpoints.chartHeightBreakpoints) {
        if (pair[0] == 'default') return pair[1] as number;
        if (clientWidth < pair[0]) return pair[1] as number;
    }
}

interface DataForChart {
    x: number,
    y: number,
    y0?: number,
    forTooltip?: string,
    label: string,
    isArtificial: boolean
}

